import { useCallback, useEffect, useState } from "react";
import { api, apiError, eur, dateFmt } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Trash2, CalendarClock } from "lucide-react";
import { DatePicker, NumberInput } from "@/components/common";
import { useI18n } from "@/i18n";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BADGE = { geplant: "bg-chart-3/20 text-chart-3", aktiv: "bg-primary/15 text-primary", historisch: "bg-muted text-muted-foreground" };

export const PriceSchedule = ({ entityType, entityId, taxRate = 19, taxRates = [0, 7, 19], onApplied }) => {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [net, setNet] = useState("");
  const [tax, setTax] = useState(taxRate);
  const [from, setFrom] = useState("");

  const load = useCallback(() => {
    if (!entityId) return;
    api.get("/price-schedules", { params: { entity_type: entityType, entity_id: entityId } })
      .then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [entityType, entityId]);
  useEffect(() => { setTax(taxRate); }, [taxRate]);
  useEffect(() => { load(); }, [load]);

  const r2 = (n) => Math.round(n * 100) / 100;
  const gross = net === "" || isNaN(+net) ? "" : r2(+net * (1 + (+tax || 0) / 100));

  const add = async () => {
    if (!net || !from) { toast.error(t("ps.needNetDate")); return; }
    try {
      await api.post("/price-schedules", { entity_type: entityType, entity_id: entityId, price_net: +net, tax_rate: +tax, effective_from: from });
      toast.success(t("ps.planned")); setNet(""); setFrom(""); load(); onApplied && onApplied();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/price-schedules/${id}`); toast.success(t("ps.deleted")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="col-span-2 rounded-md border border-border p-3" data-testid="price-schedule">
      <div className="flex items-center gap-2 mb-2"><CalendarClock className="h-4 w-4 text-muted-foreground" /><Label>{t("ps.title")}</Label></div>
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <div><Label className="text-xs">{t("ps.newNet")}</Label><NumberInput decimals={2} className="w-28 mt-1 h-9" value={net} onChange={setNet} testId="ps-net" /></div>
        <div><Label className="text-xs">{t("set.vatPercent")}</Label>
          <Select value={String(tax)} onValueChange={(v) => setTax(+v)}>
            <SelectTrigger className="mt-1 h-9 w-24" data-testid="ps-tax"><SelectValue /></SelectTrigger>
            <SelectContent>{taxRates.map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="text-xs text-muted-foreground pb-2">{t("ps.grossLabel")} <span className="font-mono text-foreground">{gross === "" ? "—" : eur(gross)}</span></div>
        <div><Label className="text-xs">{t("ps.startDate")}</Label><div className="mt-1"><DatePicker value={from} onChange={setFrom} testId="ps-from" className="w-40 h-9" /></div></div>
        <Button size="sm" onClick={add} data-testid="ps-add">{t("ps.plan")}</Button>
      </div>
      <div className="max-h-40 overflow-y-auto">
        {rows.length === 0 ? <p className="text-xs text-muted-foreground">{t("ps.none")}</p> : (
          <table className="w-full text-sm"><tbody data-testid="ps-list">
            {rows.map((s) => (
              <tr key={s.id} className="border-t border-border">
                <td className="py-1"><span className={`rounded-full px-2 py-0.5 text-xs ${BADGE[s.status] || ""}`}>{t(`ps.status.${s.status}`, s.status)}</span></td>
                <td className="py-1">{dateFmt(s.effective_from)}</td>
                <td className="py-1 font-mono">{t("ps.netGross", { net: eur(s.price_net), gross: eur(s.price_gross), rate: s.tax_rate })}</td>
                <td className="py-1 text-right">{s.status === "geplant" && <Button size="icon" variant="ghost" onClick={() => del(s.id)} data-testid={`ps-del-${s.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>}</td>
              </tr>
            ))}
          </tbody></table>
        )}
      </div>
    </div>
  );
};
